import jwtDecode from 'jwt-decode';

import { AuthResponse } from './users/models/AuthResponse';

import { persistentStorage } from 'common/utils/persistentStorage';

const STORAGE_KEY = '__TOKENS__';

const VERSION = '1.0.0'; // Increment whenever TokenManagerValue's shape changes
export type TokenManagerValue = {
    user: AuthResponse;
    tokens: { [key: string]: AuthResponse };
};

// Full claim URI used by .NET Identity / WS-Federation
const NAME_IDENTIFIER_CLAIM =
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

export interface ParsedJwtToken {
    nameid?: string;
    sub?: string; // OIDC standard claim for subject/user ID
    [NAME_IDENTIFIER_CLAIM]?: string; // Full URI claim from .NET Identity
}

// Helper to get user ID from token (supports OIDC sub, .NET nameidentifier, and legacy nameid)
export const getUserIdFromToken = (decoded: ParsedJwtToken): string => {
    return decoded.sub || decoded[NAME_IDENTIFIER_CLAIM] || decoded.nameid || '';
};

const createTokenManager = () => {
    const emptyValue: TokenManagerValue = {
        user: {
            token: '',
            refreshToken: '',
            idToken: '',
            inactivityTimeoutMinutes: null,
        },
        tokens: {},
    };
    const storage = persistentStorage<TokenManagerValue>(
        STORAGE_KEY,
        VERSION,
        emptyValue,
        JSON.parse
    );

    return {
        getTokens: async () => {
            const value = await storage.get();
            return value;
        },
        setTokens: async (tokens: AuthResponse) => {
            const value = await storage.get();
            const decoded = jwtDecode<ParsedJwtToken>(tokens.token);
            const userId = getUserIdFromToken(decoded);

            const result = await storage.set({
                user: tokens,
                tokens: {
                    ...value.tokens,
                    [`${userId}`]: tokens,
                },
            });
            return result;
        },
        clearTokens: async () => {
            await storage.set(emptyValue);
        },
        clearTokensForUser: async (userId: string) => {
            const value = await storage.get();
            const tokenToRemove = value.tokens[userId];
            const { [userId]: _removedToken, ...tokens } = value.tokens;
            const isActiveUser =
                Boolean(tokenToRemove) &&
                value.user.token === tokenToRemove.token &&
                value.user.refreshToken === tokenToRemove.refreshToken;

            // Resolves to an Error when the write failed.
            return storage.set({
                user: isActiveUser ? emptyValue.user : value.user,
                tokens,
            });
        },
        restoreActiveUser: async (userId: string) => {
            const value = await storage.get();
            const stored = value.tokens[userId];
            if (!stored) {
                throw new Error(`No stored tokens for user ${userId}`);
            }
            await storage.set({ ...value, user: stored });
            return stored;
        },
        // Logging out always clears the active session. Per-user cached entries
        // are dropped too, EXCEPT for `preserveTokensForUserId` (the one account
        // this device has biometric quick sign-in enabled for, if any) — the
        // refresh endpoint needs that user's real (not stripped) token to
        // identify them, so a partially-blanked entry can never support
        // biometric re-auth after logout. Refresh tokens were already kept
        // forever for that purpose; this just stops pretending the access
        // token could be blanked without breaking the same feature.
        clearSession: async (preserveTokensForUserId?: string) => {
            const value = await storage.get();
            const preservedEntry = preserveTokensForUserId
                ? value.tokens[preserveTokensForUserId]
                : undefined;

            await storage.set({
                user: emptyValue.user,
                tokens: preservedEntry
                    ? { [preserveTokensForUserId as string]: preservedEntry }
                    : {},
            });
        },
        listen: storage.listen,
    };
};

export const tokenManager = createTokenManager();
